import { useIntl, FormattedMessage, defineMessage } from 'react-intl';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import { useSprings, animated } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import classNames from 'classnames';
import isString from 'lodash/isString';
import shuffle from 'lodash/shuffle';
import PropTypes from 'prop-types';
import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { PropTypes as PropTypes$1 } from '@micromag/core';
import { ScreenElement } from '@micromag/core/components';
import { useScreenSize, useScreenRenderContext, useViewerContext, useViewerWebView, usePlaybackContext, usePlaybackMediaRef } from '@micromag/core/contexts';
import { useDimensionObserver, useTrackScreenEvent } from '@micromag/core/hooks';
import { isHeaderFilled, isFooterFilled, getFooterProps, getStyleFromText, getStyleFromBox } from '@micromag/core/utils';
import Background from '@micromag/element-background';
import Button from '@micromag/element-button';
import Container from '@micromag/element-container';
import Footer from '@micromag/element-footer';
import Header from '@micromag/element-header';
import Heading from '@micromag/element-heading';
import Layout, { Spacer } from '@micromag/element-layout';
import Text from '@micromag/element-text';
import Visual from '@micromag/element-visual';

var styles = {"layout":"micromag-screen-game-sort-layout","button":"micromag-screen-game-sort-button","layoutLabelOver":"micromag-screen-game-sort-layoutLabelOver","buttonLabel":"micromag-screen-game-sort-buttonLabel","container":"micromag-screen-game-sort-container","background":"micromag-screen-game-sort-background","content":"micromag-screen-game-sort-content","emptyHeading":"micromag-screen-game-sort-emptyHeading","emptyItems":"micromag-screen-game-sort-emptyItems","heading":"micromag-screen-game-sort-heading","header":"micromag-screen-game-sort-header","footer":"micromag-screen-game-sort-footer","items":"micromag-screen-game-sort-items","item":"micromag-screen-game-sort-item","itemInner":"micromag-screen-game-sort-itemInner","sorted":"micromag-screen-game-sort-sorted","draggable":"micromag-screen-game-sort-draggable","buttonResults":"micromag-screen-game-sort-buttonResults","label":"micromag-screen-game-sort-label","isEmpty":"micromag-screen-game-sort-isEmpty","valid":"micromag-screen-game-sort-valid","invalid":"micromag-screen-game-sort-invalid","resultsVisible":"micromag-screen-game-sort-resultsVisible","layoutLabelTop":"micromag-screen-game-sort-layoutLabelTop","layoutNoLabel":"micromag-screen-game-sort-layoutNoLabel","buttonVisual":"micromag-screen-game-sort-buttonVisual","submitButton":"micromag-screen-game-sort-submitButton","results":"micromag-screen-game-sort-results"};

var propTypes = {
  layout: PropTypes.oneOf(['top', 'middle', 'bottom']),
  heading: PropTypes$1.headingElement,
  // eslint-disable-next-line react/forbid-prop-types
  items: PropTypes.array,
  spacing: PropTypes.number,
  itemsLayout: PropTypes.oneOf(['label-bottom', 'label-top', 'no-label', 'label-over']),
  itemsBoxStyle: PropTypes$1.boxStyle,
  itemsTextStyle: PropTypes$1.textStyle,
  itemsResultsTextStyle: PropTypes$1.textStyle,
  validBoxStyle: PropTypes$1.boxStyle,
  invalidBoxStyle: PropTypes$1.boxStyle,
  submitBoxStyle: PropTypes$1.boxStyle,
  submitTextStyle: PropTypes$1.textStyle,
  submitButtonLabel: PropTypes.string,
  results: PropTypes$1.textElement,
  resultsBoxStyle: PropTypes$1.boxStyle,
  background: PropTypes$1.backgroundElement,
  header: PropTypes$1.header,
  footer: PropTypes$1.footer,
  current: PropTypes.bool,
  active: PropTypes.bool,
  className: PropTypes.string
};
var defaultProps = {
  layout: 'top',
  heading: null,
  items: null,
  spacing: 20,
  itemsLayout: 'label-bottom',
  itemsBoxStyle: null,
  itemsTextStyle: null,
  itemsResultsTextStyle: null,
  validBoxStyle: null,
  invalidBoxStyle: null,
  submitBoxStyle: null,
  submitTextStyle: null,
  submitButtonLabel: null,
  results: null,
  resultsBoxStyle: null,
  background: null,
  header: null,
  footer: null,
  current: true,
  active: true,
  className: null
};
var GameSort = function GameSort(_ref) {
  var layout = _ref.layout,
    heading = _ref.heading,
    initialItems = _ref.items,
    spacing = _ref.spacing,
    itemsLayout = _ref.itemsLayout,
    itemsBoxStyle = _ref.itemsBoxStyle,
    itemsTextStyle = _ref.itemsTextStyle,
    itemsResultsTextStyle = _ref.itemsResultsTextStyle,
    validBoxStyle = _ref.validBoxStyle,
    invalidBoxStyle = _ref.invalidBoxStyle,
    submitBoxStyle = _ref.submitBoxStyle,
    submitTextStyle = _ref.submitTextStyle,
    submitButtonLabel = _ref.submitButtonLabel,
    results = _ref.results,
    resultsBoxStyle = _ref.resultsBoxStyle,
    background = _ref.background,
    header = _ref.header,
    footer = _ref.footer,
    current = _ref.current,
    active = _ref.active,
    className = _ref.className;
  var intl = useIntl();
  var _useScreenSize = useScreenSize(),
    width = _useScreenSize.width,
    height = _useScreenSize.height,
    resolution = _useScreenSize.resolution;
  var _useScreenRenderConte = useScreenRenderContext(),
    isView = _useScreenRenderConte.isView,
    isPreview = _useScreenRenderConte.isPreview,
    isPlaceholder = _useScreenRenderConte.isPlaceholder,
    isEdit = _useScreenRenderConte.isEdit;
  var _useViewerContext = useViewerContext(),
    viewerTopHeight = _useViewerContext.topHeight,
    viewerBottomHeight = _useViewerContext.bottomHeight,
    viewerBottomSidesWidth = _useViewerContext.bottomSidesWidth;
  var _useViewerWebView = useViewerWebView(),
    openWebView = _useViewerWebView.open;
  var _usePlaybackContext = usePlaybackContext(),
    muted = _usePlaybackContext.muted;
  var mediaRef = usePlaybackMediaRef(current);
  var backgroundPlaying = current && (isView || isEdit);
  var backgroundShouldLoad = current || active;
  var hasHeader = isHeaderFilled(header);
  var hasFooter = isFooterFilled(footer);
  var footerProps = getFooterProps(footer, {
    isView: isView,
    current: current,
    openWebView: openWebView,
    isPreview: isPreview
  });
  var _useDimensionObserver = useDimensionObserver(),
    headerRef = _useDimensionObserver.ref,
    _useDimensionObserver2 = _useDimensionObserver.height,
    headerHeight = _useDimensionObserver2 === void 0 ? 0 : _useDimensionObserver2;
  var _useDimensionObserver3 = useDimensionObserver(),
    footerRef = _useDimensionObserver3.ref,
    _useDimensionObserver4 = _useDimensionObserver3.height,
    footerHeight = _useDimensionObserver4 === void 0 ? 0 : _useDimensionObserver4;
  var _useDimensionObserver5 = useDimensionObserver(),
    itemsRef = _useDimensionObserver5.ref,
    _useDimensionObserver6 = _useDimensionObserver5.height,
    itemsHeight = _useDimensionObserver6 === void 0 ? 0 : _useDimensionObserver6;
  var trackingEnabled = isView;
  var trackEvent = useTrackScreenEvent('game-sort');
  var items = useMemo(function () {
    return (initialItems || []).map(function (item, itemIndex) {
      return _objectSpread({
        id: "item-".concat(itemIndex)
      }, item);
    });
  }, [initialItems]);
  var shouldShuffle = isView;
  var _useState = useState(shouldShuffle ? shuffle(items || []) : items || []),
    _useState2 = _slicedToArray(_useState, 2),
    sortedItems = _useState2[0],
    setSortedItems = _useState2[1];
  var sortedItemsRef = useRef(sortedItems);
  var currentItemsRef = useRef(items);
  var elementsRef = useRef({});
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    initialSorted = _useState4[0],
    setInitialSorted = _useState4[1];
  var _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    validated = _useState6[0],
    setValidated = _useState6[1];
  var initialSortedItemsRef = useRef(sortedItems);
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    resultsVisible = _useState8[0],
    setResultsVisible = _useState8[1];
  var dragEnabled = isView && active && current && validated === null;
  useEffect(function () {
    if (currentItemsRef.current !== items && !isView) {
      var newSortedItems = shouldShuffle ? shuffle(items || []) : items || [];
      setSortedItems(newSortedItems);
      sortedItemsRef.current = newSortedItems;
      currentItemsRef.current = items;
    }
  }, [items]);
  var getNewSortedItems = useCallback(function (currentItems, item, newIndex) {
    var currentIndex = currentItems.findIndex(function (it) {
      return it === item;
    });
    var newSortedItems = _toConsumableArray(currentItems);
    var currentItem = newSortedItems[newIndex];
    newSortedItems[newIndex] = item;
    newSortedItems[currentIndex] = currentItem;
    return newSortedItems;
  }, []);
  var updateIndex = useCallback(function (item, newIndex) {
    var newSortedItems = getNewSortedItems(sortedItems, item, newIndex);
    setSortedItems(newSortedItems);
    sortedItemsRef.current = newSortedItems;
  }, [sortedItems, getNewSortedItems]);
  var _useSprings = useSprings((items || []).length, function () {
      return {
        y: "0%",
        scale: 1
      };
    }, []),
    _useSprings2 = _slicedToArray(_useSprings, 2),
    springs = _useSprings2[0],
    api = _useSprings2[1];
  var updateSpring = useCallback(function (currentItems) {
    var _ref2 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      dragItem = _ref2.dragItem,
      dragY = _ref2.dragY,
      _ref2$initial = _ref2.initial,
      initial = _ref2$initial === void 0 ? false : _ref2$initial;
    var refs = currentItems.map(function (it) {
      return elementsRef.current[it.id] || null;
    });
    var initialRefs = (items || []).map(function (it) {
      return elementsRef.current[it.id] || null;
    });
    var initialHeights = initialRefs.map(function (it) {
      var _it$getBoundingClient;
      return (it === null || it === void 0 || (_it$getBoundingClient = it.getBoundingClientRect()) === null || _it$getBoundingClient === void 0 ? void 0 : _it$getBoundingClient.height) || 0;
    });
    var heights = refs.map(function (it) {
      var _it$getBoundingClient2;
      return (it === null || it === void 0 || (_it$getBoundingClient2 = it.getBoundingClientRect()) === null || _it$getBoundingClient2 === void 0 ? void 0 : _it$getBoundingClient2.height) || 0;
    });
    // console.log('heights', heights);
    api.start(function (itemIndex) {
      var item = (items || [])[itemIndex] || null;
      var sortedIndex = currentItems.findIndex(function (it) {
        return it.id === item.id;
      });
      var currentHeight = initialHeights[itemIndex] || 0;
      var currentY = initialHeights.slice(0, itemIndex).reduce(function (acc, itemHeight) {
        return acc + itemHeight;
      }, 0);

      // This works
      if (item === dragItem) {
        var _deltaY = dragY - currentY;
        return {
          y: "".concat(_deltaY / currentHeight * 100, "%"),
          scale: 1.02,
          immediate: true
        };
      }

      // if (itemIndex === sortedIndex) {
      //     return {
      //         y: `0%`,
      //         scale: 1,
      //         immediate: initial,
      //     };
      // }

      var newY = heights.slice(0, sortedIndex).reduce(function (acc, itemHeight) {
        return acc + itemHeight;
      }, 0);
      var deltaY = newY - currentY;
      return {
        y: "".concat(deltaY / currentHeight * 100, "%"),
        scale: 1,
        immediate: initial
      };
    });
    if (initial) {
      setInitialSorted(true);
    }
  }, [api]);
  useEffect(function () {
    if (!isView) {
      return;
    }
    updateSpring(sortedItems, {
      initial: initialSortedItemsRef.current === sortedItems
    });
  }, [sortedItems, itemsHeight]);
  var bind = useDrag(function (_ref3) {
    var _ref3$args = _slicedToArray(_ref3.args, 1),
      itemIndex = _ref3$args[0],
      dragActive = _ref3.active,
      _ref3$movement = _slicedToArray(_ref3.movement, 2),
      movementY = _ref3$movement[1],
      tap = _ref3.tap;
    var item = items[itemIndex] || {};
    if (tap) {
      var currentIndex = sortedItems.findIndex(function (it) {
        return it === item;
      });
      var _newIndex = currentIndex === 0 ? sortedItems.length - 1 : currentIndex - 1;
      updateIndex(item, _newIndex);
      if (trackingEnabled) {
        trackEvent('tap', "item_".concat(itemIndex));
      }
      return;
    }
    var sortedIndex = sortedItems.findIndex(function (it) {
      return it === item;
    });

    // const heights = sortedItems.map((sortedItem) => {
    //     const elementIndex = items.findIndex((it) => it === sortedItem);
    //     return elementsRef.current[elementIndex]?.getBoundingClientRect()?.height || 0;
    // });

    var refs = sortedItems.map(function (it) {
      return elementsRef.current[it.id] || null;
    });
    var heights = refs.map(function (it) {
      var _it$getBoundingClient3;
      return (it === null || it === void 0 || (_it$getBoundingClient3 = it.getBoundingClientRect()) === null || _it$getBoundingClient3 === void 0 ? void 0 : _it$getBoundingClient3.height) || 0;
    });
    var ys = heights.map(function (itemHeight, heightIndex) {
      var endY = itemHeight + heights.slice(0, heightIndex).reduce(function (acc, h) {
        return acc + h;
      }, 0);
      return {
        isStart: heightIndex === 0,
        isEnd: heightIndex === heights.length - 1,
        start: endY - itemHeight,
        end: endY
      };
    });
    var newY = ys[sortedIndex].end - heights[sortedIndex] / 2 + movementY;
    var newIndex = ys.findIndex(function (_ref4) {
      var isStart = _ref4.isStart,
        isEnd = _ref4.isEnd,
        start = _ref4.start,
        end = _ref4.end;
      return (isStart || newY >= start) && (isEnd || newY < end);
    });
    if (dragActive) {
      var newSortedItems = getNewSortedItems(sortedItemsRef.current, item, newIndex);
      sortedItemsRef.current = newSortedItems;
      updateSpring(newSortedItems, {
        dragItem: item,
        dragY: ys[sortedIndex].start + movementY
      });
    } else {
      setSortedItems(sortedItemsRef.current);
      if (trackingEnabled) {
        trackEvent('drag', "item_".concat(itemIndex));
      }
      // updateIndex(item, newIndex !== -1 ? newIndex : sortedIndex);
    }
  }, {
    axis: 'y',
    preventDefault: true,
    filterTaps: true,
    enabled: dragEnabled
  });
  var onClickSubmit = useCallback(function () {
    var newValidated = items.map(function (it, itemIndex) {
      var sortedIndex = sortedItems.findIndex(function (sortedItem) {
        return sortedItem === it;
      });
      return itemIndex === sortedIndex;
    });
    setValidated(newValidated);
    var allValid = newValidated.reduce(function (acc, isValid) {
      return acc && isValid;
    }, true);
    var invalidCount = newValidated.filter(function (isValid) {
      return !isValid;
    }).length;
    if (trackingEnabled) {
      trackEvent('submit', allValid ? 'valid' : "invalid_".concat(invalidCount));
    }
  }, [items, sortedItems, trackingEnabled, trackEvent]);
  useEffect(function () {
    if (validated === null) {
      return function () {};
    }
    var timeout = setTimeout(function () {
      setSortedItems(items);
      setResultsVisible(true);
    }, 1000);
    return function () {
      clearTimeout(timeout);
    };
  }, [validated]);
  return /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.container, _defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty({}, className, className !== null), styles.sorted, initialSorted || !isView), styles.draggable, dragEnabled), styles.resultsVisible, resultsVisible), styles.isPlaceholder, isPlaceholder)]),
    "data-screen-ready": true
  }, /*#__PURE__*/React.createElement(Container, {
    width: width,
    height: height,
    className: styles.content
  }, /*#__PURE__*/React.createElement(Layout, {
    className: styles.layout,
    verticalAlign: layout,
    fullscreen: true,
    style: !isPlaceholder ? {
      padding: spacing,
      paddingTop: (!isPreview ? viewerTopHeight : 0) + (headerHeight || spacing),
      paddingBottom: (current && !isPreview ? viewerBottomHeight : 0) + (footerHeight || spacing)
    } : null
  }, !isPlaceholder && hasHeader ? /*#__PURE__*/React.createElement("div", {
    className: styles.header,
    ref: headerRef,
    style: {
      paddingTop: spacing / 2,
      paddingBottom: spacing,
      paddingLeft: spacing,
      paddingRight: spacing,
      transform: "translate(0px, ".concat(viewerTopHeight, "px)")
    }
  }, /*#__PURE__*/React.createElement(Header, header)) : null, /*#__PURE__*/React.createElement(ScreenElement, {
    key: "title",
    placeholder: "title",
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "X7kRRa",
      defaultMessage: [{
        "type": 0,
        "value": "Heading"
      }]
    }),
    emptyClassName: styles.emptyHeading,
    isEmpty: !heading || (heading === null || heading === void 0 ? void 0 : heading.body) === ''
  }, heading ? /*#__PURE__*/React.createElement(Heading, Object.assign({
    className: classNames([styles.heading])
  }, heading)) : null), /*#__PURE__*/React.createElement("div", {
    className: styles.items,
    ref: itemsRef
  }, /*#__PURE__*/React.createElement(Spacer, {
    key: "spacer",
    size: 5
  }), /*#__PURE__*/React.createElement(ScreenElement, {
    key: "items",
    placeholder: "items",
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "03U69o",
      defaultMessage: [{
        "type": 0,
        "value": "Buttons"
      }]
    }),
    emptyClassName: styles.emptyItems,
    isEmpty: (items || []).length === 0
  }, springs.map(function (_ref6, itemIndex) {
    var y = _ref6.y,
      scale = _ref6.scale;
    var item = items[itemIndex] || {};
    var _ref7 = item || {},
      _ref7$id = _ref7.id,
      id = _ref7$id === void 0 ? null : _ref7$id,
      _ref7$visual = _ref7.visual,
      visual = _ref7$visual === void 0 ? null : _ref7$visual,
      itemLabel = _ref7.label,
      _ref7$boxStyle = _ref7.boxStyle,
      boxStyle = _ref7$boxStyle === void 0 ? null : _ref7$boxStyle,
      _ref7$results = _ref7.results,
      itemResults = _ref7$results === void 0 ? null : _ref7$results;
    var finalLabel = isString(itemLabel) ? {
      body: itemLabel
    } : itemLabel || {};
    var _ref8 = finalLabel || {},
      _ref8$body = _ref8.body,
      label = _ref8$body === void 0 ? null : _ref8$body,
      labelTextStyle = _ref8.textStyle;
    var isEmpty = label === null && visual === null;
    var isValid = validated !== null && validated[itemIndex];
    return /*#__PURE__*/React.createElement(animated.div, Object.assign({
      key: "button-".concat(itemIndex),
      className: classNames([styles.item, _defineProperty(_defineProperty(_defineProperty({
        clickable: dragEnabled
      }, styles.isEmpty, isEmpty), styles.valid, validated !== null && isValid), styles.invalid, validated !== null && !isValid)]),
      ref: function ref(_ref1) {
        elementsRef.current[id] = _ref1;
      },
      style: _objectSpread(_objectSpread({
        transform: y.to(function (yValue) {
          return "translateY(".concat(yValue, ")");
        })
      }, getStyleFromText(itemsTextStyle)), getStyleFromText(labelTextStyle))
    }, bind(itemIndex)), /*#__PURE__*/React.createElement("div", {
      className: styles.itemInner
    }, /*#__PURE__*/React.createElement(animated.div, {
      className: classNames([styles.button, _defineProperty(_defineProperty(_defineProperty(_defineProperty({}, styles.layoutLabelBottom, itemsLayout === 'label-bottom'), styles.layoutLabelTop, itemsLayout === 'label-top'), styles.layoutNoLabel, itemsLayout === 'no-label'), styles.layoutLabelOver, itemsLayout === 'label-over')]),
      style: _objectSpread(_objectSpread(_objectSpread(_objectSpread({
        transform: scale.to(function (scaleValue) {
          return "scale(".concat(scaleValue, ")");
        })
      }, getStyleFromBox(itemsBoxStyle)), getStyleFromBox(boxStyle)), getStyleFromBox(validated && isValid ? validBoxStyle : null)), getStyleFromBox(validated && !isValid ? invalidBoxStyle : null))
      // onClick={(e) => onItemClick(e, item, itemIndex)}
    }, visual !== null ? /*#__PURE__*/React.createElement(Visual, {
      className: styles.buttonVisual,
      imageClassName: styles.thumbnail,
      media: visual,
      width: "auto"
    }) : null, label !== null || itemResults !== null && resultsVisible ? /*#__PURE__*/React.createElement("div", {
      className: styles.buttonLabel
    }, label !== null ? /*#__PURE__*/React.createElement(Text, Object.assign({}, finalLabel, {
      textStyle: _objectSpread(_objectSpread({}, itemsTextStyle), labelTextStyle),
      className: styles.label
    })) : null, itemResults !== null && resultsVisible ? /*#__PURE__*/React.createElement(Text, Object.assign({}, itemResults, {
      className: styles.buttonResults,
      textStyle: _objectSpread(_objectSpread({}, itemsResultsTextStyle), itemResults.textStyle)
    })) : null) : null)));
  }))), resultsVisible && results !== null ? /*#__PURE__*/React.createElement("div", {
    className: styles.results,
    style: _objectSpread({}, getStyleFromBox(resultsBoxStyle))
  }, /*#__PURE__*/React.createElement(Text, results)) : /*#__PURE__*/React.createElement(Button, {
    className: styles.submitButton,
    disabled: validated !== null,
    type: "button",
    onClick: onClickSubmit,
    style: _objectSpread(_objectSpread({}, getStyleFromBox(submitBoxStyle)), getStyleFromText(submitTextStyle))
  }, submitButtonLabel || intl.formatMessage({
    id: "IbZVf0",
    defaultMessage: [{
      "type": 0,
      "value": "Submit"
    }]
  })), !isPlaceholder && hasFooter ? /*#__PURE__*/React.createElement("div", {
    className: styles.footer,
    ref: footerRef,
    style: {
      paddingTop: spacing,
      paddingBottom: spacing / 2,
      paddingLeft: Math.max(viewerBottomSidesWidth - spacing, 0),
      paddingRight: Math.max(viewerBottomSidesWidth - spacing, 0)
    }
  }, /*#__PURE__*/React.createElement(Footer, footerProps)) : null)), !isPlaceholder ? /*#__PURE__*/React.createElement(Background, {
    background: background,
    width: width,
    height: height,
    resolution: resolution,
    playing: backgroundPlaying,
    muted: muted,
    shouldLoad: backgroundShouldLoad,
    mediaRef: mediaRef,
    withoutVideo: isPreview,
    className: styles.background
  }) : null);
};
GameSort.propTypes = propTypes;
GameSort.defaultProps = defaultProps;

// import * as transforms from './transforms/index';

var definition = {
  id: 'game-sort',
  type: 'screen',
  group: {
    label: defineMessage({
      id: "w3+kFW",
      defaultMessage: [{
        "type": 0,
        "value": "Game"
      }]
    }),
    order: 12
  },
  title: defineMessage({
    id: "eJMcXw",
    defaultMessage: [{
      "type": 0,
      "value": "Sort game"
    }]
  }),
  component: GameSort,
  layouts: ['top', 'middle', 'bottom'],
  // transforms,
  fields: [{
    name: 'layout',
    type: 'screen-layout',
    defaultValue: 'middle',
    label: defineMessage({
      id: "4iBXj2",
      defaultMessage: [{
        "type": 0,
        "value": "Layout"
      }]
    })
  }, {
    name: 'heading',
    type: 'heading-element',
    label: defineMessage({
      id: "wx5PI0",
      defaultMessage: [{
        "type": 0,
        "value": "Heading"
      }]
    })
  }, {
    name: 'items',
    type: 'game-sort-items',
    label: defineMessage({
      id: "fbUW2U",
      defaultMessage: [{
        "type": 0,
        "value": "Items"
      }]
    }),
    isSection: true
  }, {
    label: defineMessage({
      id: "83959F",
      defaultMessage: [{
        "type": 0,
        "value": "Items style"
      }]
    }),
    type: 'fields',
    isList: true,
    isSection: true,
    fields: [{
      name: 'itemsLayout',
      type: 'button-layout',
      label: defineMessage({
        id: "OPxJx/",
        defaultMessage: [{
          "type": 0,
          "value": "Layout"
        }]
      })
    }, {
      name: 'itemsTextStyle',
      type: 'text-style-form',
      label: defineMessage({
        id: "rzXCu2",
        defaultMessage: [{
          "type": 0,
          "value": "Label style"
        }]
      })
    }, {
      name: 'resultsTextStyle',
      type: 'text-style-form',
      label: defineMessage({
        id: "ex1Bgx",
        defaultMessage: [{
          "type": 0,
          "value": "Results style"
        }]
      })
    }, {
      name: 'itemsBoxStyle',
      type: 'box-style-form',
      label: defineMessage({
        id: "p1pghO",
        defaultMessage: [{
          "type": 0,
          "value": "Box style"
        }]
      })
    }, {
      name: 'validBoxStyle',
      type: 'box-style-form',
      label: defineMessage({
        id: "ao5VYd",
        defaultMessage: [{
          "type": 0,
          "value": "Valid box style"
        }]
      })
    }, {
      name: 'invalidBoxStyle',
      type: 'box-style-form',
      label: defineMessage({
        id: "JLYWNN",
        defaultMessage: [{
          "type": 0,
          "value": "Invalid box style"
        }]
      })
    }]
  }, {
    label: defineMessage({
      id: "WjigmA",
      defaultMessage: [{
        "type": 0,
        "value": "Submit button"
      }]
    }),
    type: 'fields',
    isList: true,
    isSection: true,
    fields: [{
      name: 'submitButtonLabel',
      type: 'text',
      label: defineMessage({
        id: "Cg3LGu",
        defaultMessage: [{
          "type": 0,
          "value": "Label"
        }]
      })
    }, {
      name: 'submitBoxStyle',
      type: 'box-style-form',
      label: defineMessage({
        id: "nno1Pq",
        defaultMessage: [{
          "type": 0,
          "value": "Button style"
        }]
      })
    }, {
      name: 'submitTextStyle',
      type: 'text-style-form',
      label: defineMessage({
        id: "OEckOD",
        defaultMessage: [{
          "type": 0,
          "value": "Text style"
        }]
      })
    }]
  }, {
    label: defineMessage({
      id: "jINZkT",
      defaultMessage: [{
        "type": 0,
        "value": "Results"
      }]
    }),
    type: 'fields',
    isList: true,
    isSection: true,
    fields: [{
      name: 'results',
      type: 'text-element',
      theme: {
        textStyle: 'text'
      },
      label: defineMessage({
        id: "ZMS1Wv",
        defaultMessage: [{
          "type": 0,
          "value": "Text"
        }]
      })
    }, {
      name: 'resultsBoxStyle',
      type: 'box-style-form',
      label: defineMessage({
        id: "p1pghO",
        defaultMessage: [{
          "type": 0,
          "value": "Box style"
        }]
      })
    }]
  }, {
    name: 'background',
    type: 'background',
    label: defineMessage({
      id: "+MPZRu",
      defaultMessage: [{
        "type": 0,
        "value": "Background"
      }]
    })
  }, {
    name: 'header',
    type: 'header',
    label: defineMessage({
      id: "rhuDxI",
      defaultMessage: [{
        "type": 0,
        "value": "Header"
      }]
    }),
    theme: {
      badge: {
        label: {
          textStyle: 'badge'
        },
        boxStyle: 'badge'
      }
    }
  }, {
    name: 'footer',
    type: 'footer',
    label: defineMessage({
      id: "g4nybp",
      defaultMessage: [{
        "type": 0,
        "value": "Footer"
      }]
    }),
    theme: {
      callToAction: {
        label: {
          textStyle: 'cta'
        },
        boxStyle: 'cta'
      }
    }
  }]
};

export { GameSort as GameSortScreen, definition as default };
